// VideoOnlyView — vista alternativa de sesión para schemas con
// tracking_mode="video_only" (ej: mov_cuello). El paciente reproduce
// un video tutorial embebido y marca "Listo, lo hice" al terminar.
// NO requiere cámara ni MediaPipe.
//
// Sprint 3 PR3 — flow paralelo a SesionCamara. El branch lo hace
// SesionCamaraWrapper en routes.tsx antes de instanciar SesionCamara,
// para evitar inicialización de cámara/evaluator innecesaria.
//
// UX:
//   - Header con nombre del ejercicio + botón Volver.
//   - Caja warm con setup hint (consistente con SesionCamaraUI pre-session).
//   - iframe YouTube usando youtube-nocookie.com (privacy-enhanced, sin
//     cookies de tracking + sin autoplay).
//   - Textarea opcional "¿Cómo te sentiste?" con maxLength=1000 + counter.
//   - Botón primario "Listo, lo hice" que dispara guardar_sesion_completada
//     con payload mínimo (total_reps=0, scores=null, pose_analysis=null,
//     notas_paciente=draft|null).
//   - Post-success: estado "✓ ¡Listo!" durante ~800ms + navega a
//     /paciente-home.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Check, Info, Loader2 } from "lucide-react";

import { useGuardarSesionCompletada } from "@/lib/supabase/queries/useGuardarSesionCompletada";
import type { ExerciseSchema } from "@/lib/pose/types";
import { getYoutubeEmbedUrl } from "@/lib/pose/youtube";

interface Props {
  sessionId: string;
  schema: ExerciseSchema;
}

const MAX_NOTAS_LENGTH = 1000;
const DONE_FEEDBACK_MS = 800;

export function VideoOnlyView({ sessionId, schema }: Props) {
  const navigate = useNavigate();
  const [notasPaciente, setNotasPaciente] = useState("");
  const [submitState, setSubmitState] = useState<
    "idle" | "submitting" | "done"
  >("idle");
  const guardar = useGuardarSesionCompletada();

  const videoEmbedUrl = useMemo(
    () => getYoutubeEmbedUrl(schema.video_url),
    [schema.video_url],
  );

  const handleSubmit = async () => {
    if (submitState !== "idle") return;
    setSubmitState("submitting");
    const trimmed = notasPaciente.trim();
    await guardar.mutate({
      sesion_id: sessionId,
      total_reps: 0,
      score_promedio: null,
      score_minimo: null,
      score_maximo: null,
      fps_avg: 0,
      duration_sec: 0,
      pose_analysis: null,
      notas_paciente: trimmed.length > 0 ? trimmed : null,
    });
    if (guardar.error) {
      // El hook setea error en su state; mostramos abajo. Volvemos a idle
      // para permitir reintento.
      setSubmitState("idle");
      return;
    }
    // Feedback visual breve antes de navegar — sin esto el click se siente
    // brusco (loading → cambio de pantalla inmediato).
    setSubmitState("done");
    setTimeout(() => {
      navigate("/paciente-home");
    }, DONE_FEEDBACK_MS);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate("/paciente-home")}
          aria-label="Volver"
          disabled={submitState !== "idle"}
          className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate">{schema.nombre}</h1>
          <p className="text-xs text-muted-foreground truncate">
            {schema.articulacion}
          </p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {/* Setup hint */}
          {schema.setupHint && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <Info
                className="w-5 h-5 text-amber-700 mt-0.5 shrink-0"
                strokeWidth={1.5}
              />
              <p className="text-sm text-amber-900 leading-relaxed">
                {schema.setupHint}
              </p>
            </div>
          )}

          {/* Video embebido — usar nocookie + sin autoplay por privacy y
              respeto a la data del paciente. */}
          {videoEmbedUrl ? (
            <div className="rounded-2xl overflow-hidden border border-border bg-black aspect-video">
              <iframe
                src={videoEmbedUrl}
                title={`Video tutorial: ${schema.nombre}`}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              Este ejercicio no tiene video tutorial cargado. Avisale a tu
              kinesiólogo.
            </div>
          )}

          {/* Textarea opcional */}
          <div className="space-y-1.5">
            <label
              htmlFor="notas-paciente"
              className="block text-sm font-medium"
            >
              ¿Cómo te sentiste?{" "}
              <span className="text-muted-foreground font-normal">
                (opcional)
              </span>
            </label>
            <textarea
              id="notas-paciente"
              value={notasPaciente}
              onChange={(e) => setNotasPaciente(e.target.value)}
              maxLength={MAX_NOTAS_LENGTH}
              disabled={submitState !== "idle"}
              rows={3}
              placeholder="Ej: Me costó más que ayer · Sentí tensión en el cuello derecho · Bien, sin molestias"
              className="w-full px-4 py-2.5 text-sm bg-input-background border border-border rounded-2xl transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent resize-none disabled:opacity-50"
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {notasPaciente.length}/{MAX_NOTAS_LENGTH}
              </span>
            </div>
          </div>

          {/* Error inline si la RPC falló */}
          {guardar.error && submitState === "idle" && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-900"
            >
              No pudimos guardar la sesión: {guardar.error.message}. Probá de
              nuevo.
            </div>
          )}

          {/* Botón primario */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitState !== "idle"}
            className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-semibold shadow-sm transition-all duration-200 ease-out hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitState === "submitting" && (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando…
              </>
            )}
            {submitState === "done" && (
              <>
                <Check className="w-4 h-4" strokeWidth={2.5} />
                ¡Listo!
              </>
            )}
            {submitState === "idle" && "Listo, lo hice"}
          </button>
        </div>
      </div>
    </div>
  );
}

